import { motion } from "framer-motion";
import { useCallback, useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { LoginForm } from "./components/auth/LoginForm";
import { OAuthCallback } from "./components/auth/OAuthCallback";
import { BrandSplash } from "./components/brand/BrandSplash";
import { OAuthCallback as GitOAuthCallback } from "./components/git/OAuthCallback";
import { Layout } from "./components/layout/Layout";
import { MainContent } from "./components/layout/MainContent";
import { ProjectDetailsPageWrapper } from "./components/projects/ProjectDetailsPageWrapper";
import { ThemeProvider } from "./components/theme-provider";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { Toaster } from "./components/ui/toaster";
import { ProjectsView } from "./components/views/ProjectsView";
import { fadeInUp } from "./lib/motion";
import { notify } from "./lib/notify";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import AdminAuditPage from "./pages/admin/AdminAuditPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminDeploymentsPage from "./pages/admin/AdminDeploymentsPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import AdminServicesPage from "./pages/admin/AdminServicesPage";
import AdminSystemPage from "./pages/admin/AdminSystemPage";
import AdminUserDetailPage from "./pages/admin/AdminUserDetailPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import DeploymentDetailsPage from "./pages/DeploymentDetailsPage";
import DesignSystem from "./pages/DesignSystem";
import NewServicePage from "./pages/NewServicePage";
import ServiceDetailsPage from "./pages/ServiceDetailsPage";
import { initializeStore, useAppStore } from "./store/useAppStore";

// The authenticated route tree, wrapped in an ErrorBoundary that is keyed by the
// current path. Keying means each route gets a FRESH boundary, so a render crash
// on one screen is cleared automatically when the user navigates elsewhere
// (instead of the error state sticking around). Must live inside <Router> so it
// can call useLocation(). See docs/error-handling.md.
function RoutedContent() {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      {/* Keyed motion.div: remounts on every route change, replaying the
          fade-in-up entrance so each page feels intentional rather than instant. */}
      <motion.div
        key={location.pathname}
        variants={fadeInUp}
        initial="hidden"
        animate="visible"
      >
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/dashboard" element={<MainContent />} />
        <Route path="/projects" element={<ProjectsView />} />
        <Route
          path="/projects/:projectId"
          element={<ProjectDetailsPageWrapper />}
        />
        <Route
          path="/projects/:projectId/services/new"
          element={<NewServicePage />}
        />
        <Route
          path="/projects/:projectId/services/:serviceId"
          element={<ServiceDetailsPage />}
        />
        <Route
          path="/projects/:projectId/deployments/:deploymentId"
          element={<DeploymentDetailsPage />}
        />
        <Route path="/deployments" element={<MainContent />} />
        <Route path="/demo-deploy" element={<MainContent />} />
        <Route path="/builds" element={<MainContent />} />
        <Route path="/analytics" element={<MainContent />} />
        <Route path="/services" element={<MainContent />} />
        <Route path="/monitoring" element={<MainContent />} />
        <Route path="/team" element={<MainContent />} />
        <Route path="/billing" element={<MainContent />} />
        <Route path="/settings" element={<MainContent />} />
        {/* Git/provider OAuth return target. The redirect_uri sent to the
            provider is `${origin}/integrations/callback`; this hands off to the
            backend and lands the user back on the Integrations view. (More
            specific than `/integrations`, so it takes precedence.) */}
        <Route path="/integrations/callback" element={<GitOAuthCallback />} />
        <Route path="/integrations" element={<MainContent />} />
        <Route path="/design-system" element={<DesignSystem />} />
        {/* Invite acceptance works while logged in — the page handles both
            the "already authenticated" and "register on accept" cases. */}
        <Route path="/invites/:token" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
      </motion.div>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { user, isLoading, isAuthenticated } = useAppStore();

  useEffect(() => {
    initializeStore();
  }, []);

  // Listen for the "access-changed" event dispatched by the API client's 403
  // interceptor. When the user's role is revoked mid-session, all they see
  // otherwise is a silent error. This surfaces a clear toast so they know to
  // refresh rather than assuming the page is broken.
  const handleAccessChanged = useCallback(() => {
    notify.warning("Access changed", {
      description:
        "Your permissions on this project may have changed. Refresh to continue.",
    });
  }, []);

  useEffect(() => {
    window.addEventListener("avahana:access-changed", handleAccessChanged);
    return () =>
      window.removeEventListener("avahana:access-changed", handleAccessChanged);
  }, [handleAccessChanged]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground" role="status" aria-live="polite">
          Loading…
        </div>
      </div>
    );
  }

  return isAuthenticated && user ? (
    <Router>
      <Routes>
        {/* Admin console — completely isolated layout, role-gated inside AdminLayout */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:id" element={<AdminUserDetailPage />} />
          <Route path="services" element={<AdminServicesPage />} />
          <Route path="deployments" element={<AdminDeploymentsPage />} />
          <Route path="system" element={<AdminSystemPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
        </Route>
        {/* Main app — all non-admin routes */}
        <Route path="*" element={
          <Layout>
            <RoutedContent />
          </Layout>
        } />
      </Routes>
    </Router>
  ) : (
    <Router>
      <Routes>
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/auth/oauth/github/callback" element={<OAuthCallback />} />
        <Route path="/auth/oauth/gitlab/callback" element={<OAuthCallback />} />
        <Route
          path="/auth/oauth/bitbucket/callback"
          element={<OAuthCallback />}
        />
        <Route path="/auth/login" element={<LoginForm />} />
        <Route path="/design-system" element={<DesignSystem />} />
        {/* Invite acceptance — accessible without authentication so users can
            register via the invite link before they have an account. */}
        <Route path="/invites/:token" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark">
      <ErrorBoundary>
        <AppContent />
        <Toaster />
        {/* Last child, and outside the router: the page renders underneath and
            nothing waits on the animation. It removes itself from the DOM when
            it lands, so it never sits invisible intercepting clicks. */}
        <BrandSplash />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
